/**
 * SelectionRenderer — Selection boxes and handles
 * Per PLAN.md render/SelectionRenderer.js
 */

import { CONNECTION_HANDLE_SIZE } from '../utils/constants.js';

export class SelectionRenderer {
    /**
     * Render selection overlay for selected nodes
     * @param {CanvasRenderingContext2D} ctx 
     * @param {Node[]} selectedNodes 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {boolean} isDark 
     */
    renderSelection(ctx, selectedNodes, viewport, isDark = false) {
        const strokeColor = isDark ? '#3B82F6' : '#2563EB';

        for (const node of selectedNodes) {
            const bounds = node.getBounds();
            const screen = viewport.worldToScreen(bounds.x, bounds.y);
            const width = bounds.width * viewport.zoom;
            const height = bounds.height * viewport.zoom;

            // Selection outline only (no handles for cleaner look)
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 2;
            ctx.setLineDash([]);

            const padding = 4;
            ctx.strokeRect(
                screen.x - padding,
                screen.y - padding,
                width + padding * 2,
                height + padding * 2
            );
        }
    }

    /**
     * Render connection handles on node edges
     * @param {CanvasRenderingContext2D} ctx 
     * @param {number} x - Screen X
     * @param {number} y - Screen Y
     * @param {number} width 
     * @param {number} height 
     * @param {string} accentColor 
     * @param {boolean} isDark 
     */
    renderConnectionHandles(ctx, x, y, width, height, accentColor, isDark) {
        const handleSize = CONNECTION_HANDLE_SIZE;
        const bgColor = isDark ? '#1E1E1E' : '#FFFFFF';

        const positions = [
            { x: x + width / 2, y: y },                    // Top
            { x: x + width, y: y + height / 2 },           // Right
            { x: x + width / 2, y: y + height },           // Bottom
            { x: x, y: y + height / 2 }                    // Left
        ];

        for (const pos of positions) {
            // Background
            ctx.fillStyle = bgColor;
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, handleSize / 2 + 2, 0, Math.PI * 2);
            ctx.fill();

            // Handle
            ctx.fillStyle = accentColor;
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, handleSize / 2, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    /**
     * Render drag selection box
     * @param {CanvasRenderingContext2D} ctx 
     * @param {number} x1 - Start screen X
     * @param {number} y1 - Start screen Y
     * @param {number} x2 - End screen X
     * @param {number} y2 - End screen Y
     * @param {boolean} isDark 
     */
    renderSelectionBox(ctx, x1, y1, x2, y2, isDark = false) {
        const strokeColor = isDark ? '#3B82F6' : '#2563EB';
        const fillColor = isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(37, 99, 235, 0.1)';

        const x = Math.min(x1, x2);
        const y = Math.min(y1, y2);
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y2 - y1);

        ctx.fillStyle = fillColor;
        ctx.fillRect(x, y, width, height);

        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(x, y, width, height);
        ctx.setLineDash([]);
    }

    /**
     * Render group bounding box
     * @param {CanvasRenderingContext2D} ctx 
     * @param {Node[]} groupNodes 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {boolean} isDark 
     */
    renderGroupBounds(ctx, groupNodes, viewport, isDark = false) {
        if (groupNodes.length === 0) return;

        // Calculate bounds
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const node of groupNodes) {
            const bounds = node.getBounds();
            minX = Math.min(minX, bounds.x);
            minY = Math.min(minY, bounds.y);
            maxX = Math.max(maxX, bounds.x + bounds.width);
            maxY = Math.max(maxY, bounds.y + bounds.height);
        }

        const padding = 12;
        const screen = viewport.worldToScreen(minX - padding, minY - padding);
        const width = (maxX - minX + padding * 2) * viewport.zoom;
        const height = (maxY - minY + padding * 2) * viewport.zoom;

        const fillColor = isDark ? 'rgba(59, 130, 246, 0.05)' : 'rgba(37, 99, 235, 0.05)';
        const strokeColor = isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(37, 99, 235, 0.3)';

        ctx.fillStyle = fillColor;
        ctx.fillRect(screen.x, screen.y, width, height);

        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(screen.x, screen.y, width, height);
        ctx.setLineDash([]);
    }
}
